import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { Alert, Steps } from 'antd'
import { isArray, isFunction, isObject, values } from 'lodash'
import { faCheckDouble, faFileEdit, faListCheck, faSchool, faUserGraduate, faUserTie } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import Cookie from 'js-cookie'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { getIdOrNull, isNonEmptyArray, isOptimistic, isUUID, safeSpread } from '../../../utils'
import { Properties } from '../../../utils/entities'
import useRequest from '../../../hooks/useRequest'
import { AS_OBJECT, getAllSections } from '../../../utils/api/section'
import { getAllCountries } from '../../../utils/api/countries'
import { getAllSectionToYears } from '../../../utils/api/sectionToYear'
import WithFormFooter from '../../../HOC/Forms/WithFormFooter'
import StudentForm from '../StudentForm/StudentForm'
import { Permissions } from '../StudentForm/SearchStudentModalProvider'
import SchoolForm from './School/SchoolForm'
import { findSimilarSchools, isDescribedSchool, PortalSchoolValidators, School, SchoolValidators } from '../../../utils/entities/school'
import { Student, StudentProperties, StudentValidators } from '../../../utils/entities/student'
import { isValid } from '../../../utils/validators'
import { reduceBySection } from '../../../utils/entities/sectionToYear'
import InternshipRequestForm from './InternshipRequestForm'
import {
  InternshipRequest, MAX_PLACES_PER_SUBMISSION, mergeWithSchool, mergeWithStudent, Origins, PortalInternshipStepValidators, PortalSchoolRequestValidators
} from '../../../utils/entities/internshipRequest'
import { selectActiveLanguage } from '../../../reducers/LanguageReducer'
import { getPortalHospitalDepartments } from '../../../utils/api/hospitalDepartment'
import { ACCESS_KEY, getPortalAddressee, getPortalOwnerName } from '../../../utils/api/internshipRequestPortal'
import { Institution } from '../../../utils/entities/institution'
import { DEFAULT_DATA as DEFAULT_FILES } from './InternshipRequestFilesForm'
import { getAllLanguages } from '../../../utils/api/language'
import { getAllSchoolsPublic, schoolExistsPublic } from '../../../utils/api/school'
import SchoolSelector from './School/SchoolSelector'
import { getInstitutionOptionsEnabledByInstitution } from '../../../utils/api/institution'
import { InstitutionOptions } from '../../../utils/constants'
import { InternshipFileTypes } from '../../../utils/entities/internshipRequestFile'
import { publicAddInternshipRequestFile, publicCreateInternshipRequest, publicGetStudentRequestLimit } from '../../../utils/api/internshipRequest'
import { ARE_PARENT_EXCLUDED, getAllSchoolSectionsPublic } from '../../../utils/api/schoolSection'
import { usePublicRequest } from '../../../hooks/usePublicRequest'
import { getMetadataByInstitution } from '../../../utils/api/internshipRequestMetadata'
import { getRedirectAction } from '../../../utils/api/student'
import { Routes } from '../../../routes'
import { normalizeName } from '../../../utils/string'
import { RequestPortalContext } from '../../RequestPortalProvider'
import { StorageKeys } from '../../AuthProvider'
import RedirectStudentModal from './RedirectStudentModal'
import ActivationPendingModal from '../../../Views/InternshipRequestPortal/ActivationPendingModal'
import ConfirmSchoolModal from '../../../Views/InternshipRequestPortal/ConfirmSchoolModal'
import SchoolAlreadyRegisteredScreen from './School/SchoolAlreadyRegisteredScreen'
import SchoolPortalSuccessScreen from './School/SchoolPortalSuccessScreen'
import { Cookies } from '../../../utils/Cookie'
import { getAllAreasPublic } from '../../../utils/api/area'
import {
  checkRequesterEmail, RequesterActions, SCHOOL_ALREADY_EXISTS, SUBMIT_ERROR_PREFIX, submitSchoolPortalRequest
} from '../../../utils/api/schoolPortalRequest'
import {
  CoordinatorValidators, PortalCoordinator, SchoolPortalRequest
} from '../../../utils/entities/schoolPortalRequest'
import SchoolPortalSteps, { SchoolSteps } from './School/SchoolPortalSteps'
import { TOTAL_PLACES_FIELD } from './School/PlacesNeedsTable'
import { DAY } from '../../../utils/momentjs'
import moment from 'moment'
import {
  displayErrorMessage, getErrorBody, onError, onSuccess, onWarning, translateErrorCode
} from '../../../utils/apiHelper'

export const InternshipRequestPortalFormContext = createContext()

export const Modes = Object.freeze({
  STUDENT: 'student',
  SCHOOL: 'school'
})

export const Forms = Object.freeze({
  COORDINATOR: Properties.COORDINATOR,
  INTERNSHIP: Properties.INTERNSHIP,
  REQUESTS: Properties.REQUESTS,
  SCHOOL: Properties.SCHOOL,
  STUDENT: Properties.STUDENT
})

const STYLES = {
  next: {
    icon: faAngleRight,
    className: 'logo-colored-button blue',
    title: 'common.step.next'
  },
  previous: {
    icon: faAngleLeft,
    className: 'logo-colored-button green',
    title: 'common.step.previous'
  }
}

export const DEFAULT_DATA = {
  [Modes.SCHOOL]: {
    [Forms.COORDINATOR]: new PortalCoordinator(),
    [Forms.SCHOOL]: new School(),
    [Forms.REQUESTS]: []
  },
  [Modes.STUDENT]: {
    [Forms.STUDENT]: new Student({ language: null }),
    [Forms.SCHOOL]: new School(),
    [Forms.INTERNSHIP]: new InternshipRequest({ requestOrigin: Origins.WEB_PORTAL, metadata: {} })
  }
}

export const SUBMIT_REQUESTS = {
  [Modes.SCHOOL]: submitSchoolPortalRequest,
  [Modes.STUDENT]: publicCreateInternshipRequest
}

const DEFAULT_FORM_HANDLER = { data: null, callback: null, mode: null }
const REQUESTED_INSTITUTION_OPTIONS = [InstitutionOptions.RESUME, InstitutionOptions.COVER_LETTER]

const InternshipRequestPortalFormProvider = ({
  children, allowSchoolCreation = false, institution, institutionGroup, portalAccessKey, visible = false, Wrapper = Fragment, onReset,
  initialMode = null, onHasAccount
}) => {
  const { setStudentEmail } = useContext(RequestPortalContext)

  const t = useSelector(selectTranslate)
  const language = useSelector(selectActiveLanguage)
  const history = useHistory()

  const buildDefaultFormHandler = useCallback(mode => (mode && DEFAULT_DATA[mode])
    ? { data: DEFAULT_DATA[mode], callback: null, mode }
    : { ...DEFAULT_FORM_HANDLER }
  , [])

  const [formHandler, setFormHandler] = useState(() => buildDefaultFormHandler(initialMode))
  const [selectedArea, setSelectedArea] = useState(null)
  const [requesterAction, setRequesterAction] = useState('')
  const [submittedEmail, setSubmittedEmail] = useState(null)
  const [isCheckingRequester, setIsCheckingRequester] = useState(false)
  const addressee = useMemo(() => getPortalAddressee({ institution, institutionGroup }), [institution, institutionGroup])

  const [duplicateSchools, setDuplicateSchools] = useState(null)
  const [overriddenDuplicate, setOverriddenDuplicate] = useState(null)
  const [isCheckingSchool, setIsCheckingSchool] = useState(false)
  const [internshipFiles, setInternshipFiles] = useState(DEFAULT_FILES)
  const [institutionOptions, setInstitutionOptions] = useState([])
  const [redirectAction, setRedirectAction] = useState('')

  const mandatoryFields = useMemo(() => institutionOptions.reduce((acc, option) => {
    if (option.optionType.type === InstitutionOptions.RESUME && option.optionEnabled) {
      acc[InternshipFileTypes.RESUME] = true
    } else if (option.optionType.type === InstitutionOptions.COVER_LETTER && option.optionEnabled) {
      acc[InternshipFileTypes.COVER_LETTER] = true
    }
    return acc
  }, {}), [institutionOptions])

  const externalHandlerSetter = formHandler => {
    if (isObject(formHandler.data) && formHandler.mode === Modes.STUDENT && getIdOrNull(institution)) {
      formHandler.data[Forms.INTERNSHIP] = new InternshipRequest({ ...formHandler.data[Forms.INTERNSHIP], institution: new Institution(institution) })
    }

    setFormHandler(formHandler)
  }

  const [currentStep, setCurrentStep] = useState(0)
  const [errorFields, setErrorFields] = useState([])
  const [isRequestLimitReached, setIsRequestLimitReached] = useState(false)
  const [isCheckingLimit, setIsCheckingLimit] = useState(false)

  useEffect(() => {
    setFormHandler(buildDefaultFormHandler(initialMode))
    setErrorFields([])
    setInternshipFiles(DEFAULT_FILES)
    setCurrentStep(0)
  }, [buildDefaultFormHandler, initialMode])

  const countries = useRequest({
    request: getAllCountries,
    parameters: useMemo(() => ({}), [])
  })

  const languages = useRequest({
    request: getAllLanguages,
    parameters: useMemo(() => ({}), [])
  })

  const schools = useRequest({
    request: getAllSchoolsPublic,
    parameters: useMemo(() => ({}), [])
  })

  const isSchoolRegistered = isObject(formHandler.data?.school) && !isOptimistic(getIdOrNull(formHandler.data?.school))

  const schoolSections = useRequest({
    request: getAllSchoolSectionsPublic,
    isPublicCall: true,
    parameters: useMemo(() => ({
      school: getIdOrNull(formHandler.data?.school),
      [ARE_PARENT_EXCLUDED]: true
    }), [formHandler.data?.school]),
    enabled: isSchoolRegistered
  })

  const sections = useRequest({
    request: getAllSections,
    isPublicCall: true,
    parameters: useMemo(() => ({ [AS_OBJECT]: false }), [])
  })

  const areas = useRequest({
    request: getAllAreasPublic,
    isPublicCall: true,
    parameters: useMemo(() => ({}), [])
  })

  const areaSections = useRequest({
    request: getAllSections,
    isPublicCall: true,
    parameters: useMemo(() => ({ [AS_OBJECT]: false, area: selectedArea }), [selectedArea]),
    enabled: formHandler.mode === Modes.SCHOOL && selectedArea !== null
  })

  const schoolPortalSections = useMemo(
    () => isNonEmptyArray(areaSections.data) ? areaSections.data : sections.data,
    [areaSections.data, sections.data]
  )

  const firstAllowedDate = useMemo(() => moment().add(1, DAY), [])

  const sectionToYears = useRequest({
    request: getAllSectionToYears,
    isPublicCall: true,
    parameters: useMemo(() => ({ paginate: false }), [])
  })

  const metadataFields = useRequest({
    request: usePublicRequest(getMetadataByInstitution),
    parameters: useMemo(() => institution, [institution]),
    enabled: getIdOrNull(institution) !== null
  })

  const availableSectionsAndYears = useMemo(() => {
    const available = {
      sections: sections.data,
      sectionToYears: sectionToYears.data
    }

    if (isSchoolRegistered && isNonEmptyArray(schoolSections.data)) {
      const filteredKeys = schoolSections.data.reduce((acc, ssec) => {
        if (ssec.sectionToYear !== null) {
          const sectionToYear = available.sectionToYears.find(sty => getIdOrNull(sty) === getIdOrNull(ssec.sectionToYear))

          acc.sections[getIdOrNull(sectionToYear.section)] = true
          acc.sectionToYears[getIdOrNull(sectionToYear)] = true
        }

        return acc
      }, { sections: {}, sectionToYears: {} })

      available.sections = available.sections.filter(s => filteredKeys.sections[getIdOrNull(s)])
      available.sectionToYears = available.sectionToYears.filter(sty => filteredKeys.sectionToYears[getIdOrNull(sty)])
    }

    return {
      sections: available.sections,
      sectionToYears: reduceBySection(available.sectionToYears)
    }
  }, [isSchoolRegistered, schoolSections.data, sections.data, sectionToYears.data])

  useEffect(() => {
    setSelectedArea(getIdOrNull(formHandler.data?.[Forms.SCHOOL]?.[Properties.AREA]))
  }, [formHandler.data?.[Forms.SCHOOL]?.[Properties.AREA]])

  useEffect(() => {
    let active = true

    if (isObject(institution) && !isOptimistic(institution)) {
      getInstitutionOptionsEnabledByInstitution(institution, REQUESTED_INSTITUTION_OPTIONS).then(({ data }) => {
        if (active) {
          setInstitutionOptions(data)
        }
      }).catch(() => {
        if (active) {
          setInstitutionOptions([])
        }
      })
    }

    return () => {
      active = false
    }
  }, [institution])

  const requestInstitution = useMemo(() => getIdOrNull(formHandler.data?.[Forms.INTERNSHIP]?.institution)
    ? formHandler.data[Forms.INTERNSHIP].institution
    : institution
  , [formHandler.data?.[Forms.INTERNSHIP]?.institution, institution])
  const requestInstitutionId = getIdOrNull(requestInstitution)
  const requesterEmail = (formHandler.data?.[Forms.STUDENT]?.[Properties.EMAIL] ?? '').trim()

  const checkRequestLimit = institutionToCheck => {
    const check = requesterEmail === '' || getIdOrNull(institutionToCheck) === null
      ? Promise.resolve(false)
      : publicGetStudentRequestLimit(institutionToCheck, requesterEmail, portalAccessKey)
        .then(({ data }) => data?.reached === true)
        .catch(() => false)

    return check.then(reached => {
      setIsRequestLimitReached(reached)

      return reached
    })
  }

  useEffect(() => {
    setIsRequestLimitReached(false)
  }, [requesterEmail])

  useEffect(() => {
    if (formHandler.mode === Modes.STUDENT && currentStep === 2) {
      checkRequestLimit(requestInstitution)
    }
  }, [formHandler.mode, currentStep, requestInstitutionId])

  const hospitalDepartments = useRequest({
    request: getPortalHospitalDepartments,
    parameters: useMemo(() => ({
      institution: getIdOrNull(requestInstitution),
      [ACCESS_KEY]: portalAccessKey,
      paginate: false
    }), [requestInstitution, portalAccessKey]),
    enabled: formHandler.mode !== null && (getIdOrNull(requestInstitution) !== null) && portalAccessKey !== null
  })

  const updateKey = key => value => setFormHandler(formHandler => ({
    ...formHandler,
    data: {
      ...formHandler.data,
      [key]: { ...safeSpread(formHandler.data[key]), ...value }
    }
  }))

  const setRequests = requests => setFormHandler(formHandler => ({
    ...formHandler,
    data: { ...formHandler.data, [Forms.REQUESTS]: requests }
  }))

  const searchStudent = email => getRedirectAction(email).then(({ data }) => {
    setRedirectAction(data)
    setStudentEmail(email)
  }).catch(_ => undefined)

  const handleSelectSchool = value => {
    setFormHandler(formHandler => ({
      ...formHandler,
      data: {
        ...formHandler.data,
        [Properties.SCHOOL]: value,
        [Forms.INTERNSHIP]: new InternshipRequest({
          ...formHandler.data[Forms.INTERNSHIP],
          [Properties.SECTION_TO_YEAR]: null
        })
      }
    }))

    setErrorFields([])
  }

  const handleMetadataChange = (fieldName, value) => {
    setFormHandler(formHandler => ({
      ...formHandler,
      data: {
        ...formHandler.data,
        [Forms.INTERNSHIP]: {
          ...formHandler.data[Forms.INTERNSHIP],
          [Properties.METADATA]: {
            ...formHandler.data[Forms.INTERNSHIP][Properties.METADATA],
            [fieldName]: value
          }
        }
      }
    }))
  }

  const isValidStep = validation => isFunction(validation) ? validation().keys.length === 0 : true

  const nextStep = validation => () => {
    if (isValidStep(validation)) {
      setCurrentStep(step => step + 1)
    }
  }

  const previousStep = () => setCurrentStep(step => Math.max(step - 1, 0))
  const resetHandler = () => {
    setErrorFields([])
    setDuplicateSchools(null)
    setOverriddenDuplicate(null)
    setRequesterAction('')
    setSubmittedEmail(null)
    setFormHandler(buildDefaultFormHandler(initialMode))
    setInternshipFiles(DEFAULT_FILES)

    if (isFunction(onReset)) {
      onReset()
    }
  }

  const validatedInternshipForm = (data, validators) => () => {
    const errors = validateForm(data, validators)()

    if (mandatoryFields[InstitutionOptions.RESUME] && internshipFiles[InternshipFileTypes.RESUME].length === 0) {
      errors.keys.push(InternshipFileTypes.RESUME)
    }

    if (mandatoryFields[InstitutionOptions.COVER_LETTER] && internshipFiles[InternshipFileTypes.COVER_LETTER].length === 0) {
      errors.keys.push(InternshipFileTypes.COVER_LETTER)
    }

    return errors
  }

  const validateForm = (data, validators) => () => {
    const errors = isValid(data, values(validators))

    setErrorFields(errors.keys)

    return errors
  }

  const validateSchool = useCallback(() => {
    const school = formHandler.data[Forms.SCHOOL]
    let errors = { keys: [] }

    if (allowSchoolCreation || isDescribedSchool(school)) {
      errors = validateForm(school, values(SchoolValidators))()
    } else {
      errors = validateForm(school, values({ id: s => isUUID(getIdOrNull(s[Properties.ID])) }))()
    }

    return errors
  }, [allowSchoolCreation, formHandler.data?.[Forms.SCHOOL]])

  const validateCoordinator = useCallback(
    () => validateForm(formHandler.data?.[Forms.COORDINATOR], values(CoordinatorValidators))(),
    [formHandler.data?.[Forms.COORDINATOR]]
  )

  const validateSchoolIdentity = useCallback(
    () => validateForm(formHandler.data?.[Forms.SCHOOL], values(PortalSchoolValidators))(),
    [formHandler.data?.[Forms.SCHOOL]]
  )

  const validateRequests = useCallback(() => {
    const requests = formHandler.data?.[Forms.REQUESTS] ?? []
    const keys = []

    if (!isNonEmptyArray(requests)) {
      keys.push(Forms.REQUESTS)
    }

    requests.forEach((line, index) => {
      isValid(line, values(PortalSchoolRequestValidators)).keys.forEach(key => keys.push(`${index}.${key}`))
    })

    if (requests.reduce((total, line) => total + (line.total ?? 0), 0) > MAX_PLACES_PER_SUBMISSION) {
      keys.push(TOTAL_PLACES_FIELD)
    }

    setErrorFields(keys)

    return { keys }
  }, [formHandler.data?.[Forms.REQUESTS]])

  const handleFormSubmit = validation => e => {
    e.preventDefault()

    if (isRequestLimitReached || !isValidStep(validation)) {
      return
    }

    const entity = { name: '', id: '' }

    if (institutionGroup) {
      entity.name = Properties.INSTITUTION_GROUP
      entity.id = getIdOrNull(institutionGroup)
    } else if (institution) {
      entity.name = Properties.INSTITUTION
      entity.id = getIdOrNull(institution)
    }

    const portalData = {
      [entity.name]: entity.id,
      [ACCESS_KEY]: portalAccessKey
    }

    const toSubmit = { data: null }

    if (formHandler.mode === Modes.STUDENT) {
      const requestFiles = Object.keys(internshipFiles).reduce(
        (acc, type) => acc.concat(internshipFiles[type].flatMap(file => ({ file, type }))), [])

      const { school, student, internship } = formHandler.data

      const request = new InternshipRequest(
        mergeWithSchool({ ...internship, institution: requestInstitution, files: requestFiles }, school)
      )
      const internshipRequest = mergeWithStudent(request, new Student({ ...student, language: language.code }))
      /** @todo remove "student" key in request body if student is optimistic */
      internshipRequest.student = null

      toSubmit.data = internshipRequest.toRequestBody()
    } else if (formHandler.mode === Modes.SCHOOL) {
      const { coordinator, school, requests } = formHandler.data

      const schoolPortalRequest = new SchoolPortalRequest({
        coordinator: { ...coordinator, languageLocale: language.code },
        school,
        requests
      })

      toSubmit.data = { ...portalData, ...schoolPortalRequest.toRequestBody() }
    }

    SUBMIT_REQUESTS[formHandler.mode]({ ...toSubmit.data, [ACCESS_KEY]: portalAccessKey }).then(({ data }) => {
      if (formHandler.mode === Modes.STUDENT) {
        const promisesStack = []

        if (isArray(toSubmit.data.files)) {
          toSubmit.data.files.forEach(f => {
            f[ACCESS_KEY] = portalAccessKey
            promisesStack.push(publicAddInternshipRequestFile(data, f))
          })
        }
      }
    }).then(() => {
      if (isFunction(formHandler.callback)) {
        formHandler.callback(toSubmit.data)
      }

      if (formHandler.mode === Modes.SCHOOL) {
        setSubmittedEmail(coordinatorEmail())
        setCurrentStep(0)

        return
      }

      resetHandler()
      setCurrentStep(0)

      onSuccess(t('internship_request_portal.submit.success'))
    }).catch(reason => {
      console.error(reason)

      if (formHandler.mode !== Modes.SCHOOL) {
        displayErrorMessage(reason, t)

        return
      }

      getErrorBody(reason).then(({ message }) => {
        if (message === SCHOOL_ALREADY_EXISTS) {
          setDuplicateSchools(findSimilarSchools(formHandler.data[Forms.SCHOOL], schools.data))
          setOverriddenDuplicate(null)
          setCurrentStep(SchoolSteps.SCHOOL)

          return
        }

        onError(translateErrorCode(t, message, SUBMIT_ERROR_PREFIX, `${SUBMIT_ERROR_PREFIX}.default`))
      }).catch(() => onError(t(`${SUBMIT_ERROR_PREFIX}.default`)))
    })
  }

  const coordinatorEmail = () => (formHandler.data?.[Forms.COORDINATOR]?.[Properties.EMAIL] ?? '').trim()

  const handleRequesterRedirect = () => {
    setStudentEmail(coordinatorEmail())

    if (isFunction(onHasAccount)) {
      onHasAccount()
    }
  }

  const applyRequesterAction = action => {
    if (action === RequesterActions.ALLOW) {
      return true
    }

    if (action === RequesterActions.REDIRECT_TO_LOGIN) {
      handleRequesterRedirect()
    } else {
      setRequesterAction(action)
    }

    return false
  }

  const schoolSignature = school => `${normalizeName(school?.[Properties.NAME])}|${normalizeName(school?.[Properties.ABBREVIATION])}`

  const handleSchoolIdentityNext = () => {
    if (validateSchoolIdentity().keys.length !== 0) {
      return
    }

    const school = formHandler.data[Forms.SCHOOL]

    if (overriddenDuplicate === schoolSignature(school)) {
      setCurrentStep(step => step + 1)

      return
    }

    const similar = findSimilarSchools(school, schools.data)

    setIsCheckingSchool(true)

    schoolExistsPublic({
      name: school[Properties.NAME],
      abbreviation: school[Properties.ABBREVIATION],
      email: school[Properties.EMAIL]
    }).then(({ data }) => {
      if (data?.exists === true) {
        setDuplicateSchools(isNonEmptyArray(data.schools) ? data.schools : similar)
      } else {
        setCurrentStep(step => step + 1)
      }
    }).catch(() => {
      if (isNonEmptyArray(similar)) {
        setDuplicateSchools(similar)
      } else {
        setCurrentStep(step => step + 1)
      }
    }).finally(() => setIsCheckingSchool(false))
  }

  const handleDuplicateOverride = () => {
    setOverriddenDuplicate(schoolSignature(formHandler.data[Forms.SCHOOL]))
    setDuplicateSchools(null)
    setCurrentStep(step => step + 1)
  }

  const schoolStepValidators = {
    [SchoolSteps.COORDINATOR]: validateCoordinator,
    [SchoolSteps.SCHOOL]: validateSchoolIdentity,
    [SchoolSteps.REQUESTS]: validateRequests
  }

  const handleSchoolNext = () => {
    if (currentStep === SchoolSteps.SCHOOL) {
      handleSchoolIdentityNext()

      return
    }

    if (currentStep !== SchoolSteps.COORDINATOR) {
      nextStep(schoolStepValidators[currentStep])()

      return
    }

    if (validateCoordinator().keys.length !== 0) {
      return
    }

    setIsCheckingRequester(true)

    checkRequesterEmail(coordinatorEmail(), portalAccessKey, addressee)
      .then(({ data }) => {
        if (applyRequesterAction(data?.action ?? data)) {
          setCurrentStep(step => step + 1)
        }
      })
      .catch(() => {
        onWarning(t('school_portal.requester.check_unavailable'))
        setCurrentStep(step => step + 1)
      })
      .finally(() => setIsCheckingRequester(false))
  }

  const handleSchoolSubmit = e => {
    const failingStep = [SchoolSteps.COORDINATOR, SchoolSteps.SCHOOL, SchoolSteps.REQUESTS]
      .find(step => schoolStepValidators[step]().keys.length !== 0)

    if (failingStep !== undefined) {
      setCurrentStep(failingStep)

      return
    }

    handleFormSubmit(null)(e)
  }

  const steps = useMemo(() => {
    if (!(
      isNonEmptyArray(countries.data) &&
      isNonEmptyArray(sections.data) &&
      isObject(sectionToYears.data))
    ) return []

    let steps = []

    if (formHandler.mode === Modes.SCHOOL) {
      steps = steps.concat([
        {
          form: Forms.COORDINATOR,
          title: t('school_portal.steps.coordinator'),
          icon: faUserTie
        },
        {
          form: Forms.SCHOOL,
          title: t('common.school'),
          icon: faSchool
        },
        {
          form: Forms.REQUESTS,
          title: t('school_portal.steps.requests'),
          icon: faListCheck
        },
        {
          form: 'summary',
          title: t('school_portal.steps.summary'),
          icon: faCheckDouble
        }
      ])
    } else if (formHandler.mode === Modes.STUDENT) {
      steps = steps.concat([
        {
          form: Forms.STUDENT,
          title: t('common.student'),
          icon: faUserGraduate
        },
        {
          form: Forms.SCHOOL,
          title: t('common.school'),
          icon: faSchool
        },
        {
          form: Forms.INTERNSHIP,
          title: t('common.internship'),
          icon: faFileEdit
        }
      ])
    }

    return steps
  }, [formHandler.mode, formHandler.data, countries.data, sections.data, sectionToYears.data, t])

  const studentValidators = useMemo(() => {
    const validators = { ...StudentValidators }
    delete validators[StudentProperties.SECTIONS]

    return validators
  }, [])

  const handleStudentNext = () => {
    if (isCheckingLimit || !isValidStep(validateForm(formHandler.data[Forms.STUDENT], values(studentValidators)))) {
      return
    }

    setIsCheckingLimit(true)

    checkRequestLimit(institution).then(reached => {
      if (!reached) {
        setCurrentStep(step => (step === 0 ? 1 : step))
      }
    }).finally(() => setIsCheckingLimit(false))
  }

  return (
    <>
      <RedirectStudentModal
        redirectAction={redirectAction}
        onCancel={() => {
          updateKey(Forms.STUDENT)({ [Properties.EMAIL]: '' })
          setRedirectAction('')
        }}
        onConfirm={() => {
          Cookie.remove(Cookies.TOKEN)
          window.localStorage.removeItem(StorageKeys.ACCOUNT_ID)
          history.push(Routes.LOGIN)
        }}
      />

      <ActivationPendingModal
        visible={requesterAction === RequesterActions.ACTIVATION_PENDING}
        onCancel={() => setRequesterAction('')}
        onConfirm={handleRequesterRedirect}
      />

      <ConfirmSchoolModal
        visible={requesterAction === RequesterActions.CONFIRM_SCHOOL}
        onLogin={handleRequesterRedirect}
        onNewSchool={() => {
          setRequesterAction('')
          setCurrentStep(step => step + 1)
        }}
      />

      <InternshipRequestPortalFormContext.Provider value={{ formHandler, setFormHandler: externalHandlerSetter }}>
        {children}
      </InternshipRequestPortalFormContext.Provider>
      {visible && (
        <Wrapper>
          {formHandler.data && submittedEmail === null && isNonEmptyArray(steps) && (
            <Steps current={currentStep} type='navigation'>
              {steps.map(item => (
                <Steps.Step key={item.form} title={item.title} icon={<FontAwesomeIcon icon={item.icon} />} />
              ))}
            </Steps>
          )}
          {formHandler.mode === Modes.SCHOOL && submittedEmail !== null && (
            <SchoolPortalSuccessScreen
              email={submittedEmail}
              institutionName={getPortalOwnerName({ institution, institutionGroup })}
              onRestart={resetHandler}
            />
          )}
          {formHandler.mode === Modes.SCHOOL && submittedEmail === null && isNonEmptyArray(steps) && currentStep !== null && duplicateSchools !== null && (
            <SchoolAlreadyRegisteredScreen
              countries={countries.data}
              schools={duplicateSchools}
              onLogin={handleRequesterRedirect}
              onNotMySchool={handleDuplicateOverride}
            />
          )}
          {formHandler.mode === Modes.SCHOOL && submittedEmail === null && isNonEmptyArray(steps) && currentStep !== null && duplicateSchools === null && (
            <SchoolPortalSteps
              areas={areas.data}
              countries={countries.data}
              currentStep={currentStep}
              data={formHandler.data}
              errorFields={errorFields}
              hospitalDepartments={hospitalDepartments.data}
              institution={institution}
              institutionGroup={institutionGroup}
              minDate={firstAllowedDate}
              sections={schoolPortalSections}
              sectionToYears={availableSectionsAndYears.sectionToYears}
              selectedArea={selectedArea}
              onAreaChange={setSelectedArea}
              onCancel={resetHandler}
              isCheckingRequester={isCheckingRequester}
              isCheckingSchool={isCheckingSchool}
              onCoordinatorChange={updateKey(Forms.COORDINATOR)}
              onNext={handleSchoolNext}
              onPrevious={previousStep}
              onRequestsChange={setRequests}
              onSchoolChange={updateKey(Forms.SCHOOL)}
              onSubmit={handleSchoolSubmit}
            />
          )}
          {formHandler.mode === Modes.STUDENT && isNonEmptyArray(steps) && currentStep !== null && (
            <>
              <WithFormFooter
                visible={currentStep === 0}
                confirmClassName={STYLES.next.className}
                confirmTitle={STYLES.next.title}
                confirmIcon={STYLES.next.icon}
                disabled={isCheckingLimit}
                onConfirm={handleStudentNext}
                onCancel={resetHandler}
              >
                <div className='pd-8'>
                  <StudentForm
                    errorFields={errorFields}
                    languages={languages.data}
                    permission={Permissions.WRITE}
                    data={formHandler.data[Forms.STUDENT]}
                    onChange={updateKey(Forms.STUDENT)}
                    onEmailChange={searchStudent}
                  />
                  {isRequestLimitReached && (
                    <Alert
                      className='request-limit-alert'
                      type='error'
                      showIcon
                      message={t('student_internship_request_limit_reached')}
                    />
                  )}
                </div>
              </WithFormFooter>

              <WithFormFooter
                cancelTitle={STYLES.previous.title}
                cancelIcon={STYLES.previous.icon}
                visible={currentStep === 1}
                confirmClassName={STYLES.next.className}
                confirmTitle={STYLES.next.title}
                confirmIcon={STYLES.next.icon}
                onConfirm={nextStep(validateSchool)}
                onCancel={previousStep}
              >
                {allowSchoolCreation ? (
                  <SchoolForm
                    errorFields={errorFields}
                    displayTitle={false}
                    countries={countries.data}
                    data={formHandler.data[Forms.SCHOOL]}
                    onChange={updateKey(Forms.SCHOOL)}
                  />
                ) : (
                  <SchoolSelector
                    title={t('internship_request_portal.selectors.school.title')}
                    countries={countries.data}
                    errorFields={errorFields}
                    schools={schools.data}
                    data={formHandler.data[Forms.SCHOOL]}
                    onChange={handleSelectSchool}
                  />
                )}
              </WithFormFooter>

              <WithFormFooter
                visible={currentStep === 2}
                cancelTitle={STYLES.previous.title}
                cancelIcon={STYLES.previous.icon}
                confirmClassName={STYLES.previous.className}
                disabled={isRequestLimitReached}
                onConfirm={handleFormSubmit(validatedInternshipForm(
                  formHandler.data[Forms.INTERNSHIP],
                  values(PortalInternshipStepValidators)
                ))}
                onCancel={previousStep}
              >
                {isRequestLimitReached && (
                  <Alert
                    className='request-limit-alert'
                    style={{ marginBottom: 8 }}
                    type='error'
                    showIcon
                    message={t('student_internship_request_limit_reached')}
                  />
                )}
                <InternshipRequestForm
                  data={formHandler.data[Forms.INTERNSHIP]}
                  displayTitle={false}
                  errorFields={errorFields}
                  files={internshipFiles}
                  hospitalDepartments={hospitalDepartments.data}
                  institution={institution}
                  institutionGroup={institutionGroup}
                  requiredFields={mandatoryFields}
                  sections={availableSectionsAndYears.sections}
                  sectionToYears={availableSectionsAndYears.sectionToYears}
                  metadataFields={metadataFields.data}
                  onMetadataChange={handleMetadataChange}
                  onChange={updateKey(Forms.INTERNSHIP)}
                  setFiles={setInternshipFiles}
                />
              </WithFormFooter>
            </>
          )}
        </Wrapper>
      )}
    </>
  )
}

export default InternshipRequestPortalFormProvider
